import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BalanceSheetPageView } from './BalanceSheetPageView';
import { useBalanceSheetPage } from './useBalanceSheetPage';

/**
 * `/accounting/statements/balance-sheet` — the Balance Sheet screen. Owns its
 * data hook and an `ErrorBoundary`; the throw lives in the inner Content child
 * because React boundaries don't catch errors thrown by the component that
 * renders them (the repo's self-served page pattern).
 */
export const BalanceSheetPage: React.FC = () => {
    const page = useBalanceSheetPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('statements.error.title'),
            errorMessage: t('statements.error.message'),
            errorRetry: t('statements.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('balance-sheet-page')}
        >
            <ErrorBoundary
                data-testid="balance-sheet-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="balance-sheet-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <BalanceSheetPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type BalanceSheetPageContentProps = {
    page: ReturnType<typeof useBalanceSheetPage>;
};

const BalanceSheetPageContent: React.FC<BalanceSheetPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its as-of control and Print CTA while loading and skeletons
    // only the statement body — the repo's loading-state standard.
    return (
        <BalanceSheetPageView
            isLoading={page.isLoading || page.rows === null}
            balance={page.balance}
            generatedText={page.generatedText}
            onDismissWarnings={page.onDismissWarnings}
            onViewLedger={page.onViewLedger}
            columns={page.columns}
            rows={page.rows ?? []}
            unmappedCodes={page.unmappedCodes}
            warningNotices={page.warningNotices}
        />
    );
};
